import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Check,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { API_URL } from '../config/api';

export default function Results() {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [flippedCards, setFlippedCards] = useState({});
  const [openQuestions, setOpenQuestions] = useState({});

  const fetchContent = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/${contentId}`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, contentId, navigate]);

  useEffect(() => {
    if (accessToken && contentId) {
      fetchContent();
    }
  }, [accessToken, contentId, fetchContent]);

  const toggleCard = (id) => {
    setFlippedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleQuestion = (id) => {
    setOpenQuestions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!content) return null;

  const renderExplanation = () => {
    const data = content.content;
    return (
      <div className="space-y-6">
        <h2 className="font-heading font-black text-2xl">
          {typeof data.title === 'string'
            ? data.title
            : 'Concept Explanation'}
        </h2>

        {Array.isArray(data.concepts) &&
          data.concepts.map((concept, index) => (
            <motion.div
              key={index}
              className="border-2 border-border border-l-4 border-l-primary p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <h3 className="font-heading font-bold text-lg mb-3">{typeof concept.name === 'string'
                ? concept.name
                : JSON.stringify(concept.name)}
              </h3>
              <div className="text-muted-foreground mb-4">
                {concept.explanation && typeof concept.explanation === 'object' ? (
                  <>
                    <p>
                      {typeof concept.explanation.description === 'string'
                        ? concept.explanation.description
                        : JSON.stringify(concept.explanation.description)}
                    </p>

                    {concept.explanation.calculation && (
                      <p className="mt-2 font-mono">
                        {typeof concept.explanation.calculation === 'string'
                          ? concept.explanation.calculation
                          : JSON.stringify(concept.explanation.calculation)}
                      </p>
                    )}
                  </>
                ) : (
                  <p>
                    {typeof concept.explanation === 'string'
                      ? concept.explanation
                      : JSON.stringify(concept.explanation)}
                  </p>
                )}
              </div>
              {concept.formula && (
                <div className="bg-secondary p-4 mb-4 font-mono text-sm">
                  {typeof concept.formula === 'string'
                    ? concept.formula
                    : JSON.stringify(concept.formula)}
                </div>
              )}
              {concept.example && (
                <div className="mb-4">
                  <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Example</span>
                  <p className="mt-1">
                    {typeof concept.example === 'string'
                      ? concept.example
                      : JSON.stringify(concept.example)}
                  </p>
                </div>
              )}
              {concept.tips && concept.tips.length > 0 && (
                <div>
                  <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Tips</span>
                  <ul className="mt-2 space-y-1">
                    {concept.tips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        {typeof tip === 'string' ? tip : JSON.stringify(tip)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          ))}
        {data.raw_content && (
          <div className="border-2 border-border p-6 whitespace-pre-wrap">
            {data.raw_content}
          </div>
        )}
      </div>
    );
  };

  const renderQuiz = () => {
    const data = content.content;
    return (
      <div className="space-y-6">
        <h2 className="font-heading font-black text-2xl">{data.title || 'Practice Quiz'}</h2>
        {data.questions?.map((q, index) => (
          <motion.div
            key={index}
            className="border-2 border-border"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Collapsible open={openQuestions[index]} onOpenChange={() => toggleQuestion(index)}>
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">
                    {q.number || index + 1}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium">{q.question}</p>
                    {q.options && (
                      <div className="mt-3 space-y-2">
                        {q.options.map((opt, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            {opt}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <CollapsibleTrigger asChild>
                <button className="w-full border-t-2 border-border p-3 flex items-center justify-center gap-2 text-sm font-medium hover:bg-secondary transition-colors">
                  {openQuestions[index] ? (
                    <>Hide Answer <ChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>Show Answer <ChevronDown className="w-4 h-4" /></>
                  )}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 bg-secondary border-t-2 border-border">
                  <div className="mb-2">
                    <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Answer</span>
                    <p className="font-medium mt-1">{q.answer}</p>
                  </div>
                  {q.explanation && (
                    <div>
                      <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Explanation</span>
                      <p className="text-sm text-muted-foreground mt-1">{q.explanation}</p>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </motion.div>
        ))}
      </div>
    );
  };

  const renderExam = () => {
    const data = content.content;
    return (
      <div className="space-y-8">
        <div>
          <h2 className="font-heading font-black text-2xl">{data.title || 'Practice Exam'}</h2>
          {data.duration && (
            <p className="text-muted-foreground mt-1">Duration: {data.duration}</p>
          )}
          {data.total_points && (
            <p className="text-muted-foreground">Total Points: {data.total_points}</p>
          )}
        </div>
        {data.sections?.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            <h3 className="font-heading font-bold text-lg mb-4 border-b-2 border-border pb-2">
              {section.name}
            </h3>
            <div className="space-y-4">
              {section.questions?.map((q, qIndex) => (
                <motion.div
                  key={qIndex}
                  className="border-2 border-border"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: qIndex * 0.05 }}
                >
                  <Collapsible>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex items-start gap-3">
                          <span className="w-8 h-8 bg-primary text-white flex items-center justify-center font-bold flex-shrink-0 text-sm">
                            {q.number || qIndex + 1}
                          </span>
                          <p className="font-medium">{q.question}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {q.points && (
                            <span className="text-xs font-mono bg-secondary px-2 py-1">
                              {q.points} pts
                            </span>
                          )}
                          {q.difficulty && (
                            <span className={`text-xs font-mono px-2 py-1 ${q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                                q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                              }`}>
                              {q.difficulty}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <CollapsibleTrigger asChild>
                      <button className="w-full border-t-2 border-border p-3 flex items-center justify-center gap-2 text-sm font-medium hover:bg-secondary transition-colors">
                        Show Solution <ChevronDown className="w-4 h-4" />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 bg-secondary border-t-2 border-border">
                        <div className="mb-2">
                          <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Answer</span>
                          <p className="font-medium mt-1">{q.answer}</p>
                        </div>
                        {q.solution && (
                          <div>
                            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Solution</span>
                            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{q.solution}</p>
                          </div>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
        {data.questions && !data.sections && renderQuiz()}
      </div>
    );
  };

  const renderFlashcards = () => {
    const data = content.content;
    const cards = data.cards || [];

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-black text-2xl">{data.title || 'Flashcards'}</h2>
          <Button
            variant="outline"
            className="h-10 border-2"
            onClick={() => setFlippedCards({})}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset All
          </Button>
        </div>
        <p className="text-muted-foreground">Click a card to flip it</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((card, index) => (
            <motion.div
              key={card.id || index}
              className="relative h-48 cursor-pointer"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => toggleCard(card.id || index)}
              style={{ perspective: '1000px' }}
              data-testid={`flashcard-${index}`}
            >
              <motion.div
                className="w-full h-full"
                animate={{ rotateY: flippedCards[card.id || index] ? 180 : 0 }}
                transition={{ duration: 0.4 }}
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Front */}
                <div
                  className="absolute inset-0 border-2 border-border p-4 flex flex-col justify-center items-center text-center bg-white"
                  style={{ backfaceVisibility: 'hidden' }}
                >
                  {card.category && (
                    <span className="font-mono text-xs uppercase tracking-wider text-primary mb-2">
                      {card.category}
                    </span>
                  )}
                  <p className="font-heading font-bold">{card.front}</p>
                </div>
                {/* Back */}
                <div
                  className="absolute inset-0 border-2 border-primary p-4 flex flex-col justify-center items-center text-center bg-primary/5"
                  style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                >
                  <p className="text-sm">{card.back}</p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  };
  const renderTrueFalse = () => {
    const data = content.content;

    return (
      <div className="space-y-6">
        <h2 className="font-heading font-black text-2xl">
          {data.title || 'True/False Practice'}
        </h2>

        {data.items?.map((item, index) => (
          <motion.div
            key={index}
            className="border-2 border-border"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Collapsible
              open={openQuestions[index]}
              onOpenChange={() => toggleQuestion(index)}
            >
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">
                    {index + 1}
                  </span>

                  <p className="font-medium">
                    {item.statement}
                  </p>
                </div>
              </div>

              <CollapsibleTrigger asChild>
                <button className="w-full border-t-2 border-border p-3 flex items-center justify-center gap-2 text-sm font-medium hover:bg-secondary transition-colors">
                  {openQuestions[index] ? (
                    <>
                      Hide Answer <ChevronUp className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Show Answer <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </button>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="p-4 bg-secondary border-t-2 border-border">
                  <div className="mb-3 flex items-center gap-2">
                    {item.correct_answer ? (
                      <>
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="font-bold text-green-600">
                          TRUE
                        </span>
                      </>
                    ) : (
                      <>
                        <X className="w-5 h-5 text-red-600" />
                        <span className="font-bold text-red-600">
                          FALSE
                        </span>
                      </>
                    )}
                  </div>

                  {item.explanation && (
                    <div>
                      <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                        Explanation
                      </span>

                      <p className="text-sm text-muted-foreground mt-1">
                        {item.explanation}
                      </p>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </motion.div>
        ))}
      </div>
    );
  };

  const renderContent = () => {
    switch (content.study_type) {
      case 'explanation':
        return renderExplanation();
      case 'quiz':
        return renderQuiz();
      case 'exam':
        return renderExam();
      case 'flashcards':
        return renderFlashcards();
      case 'true_false':
        return renderTrueFalse();
      default:
        return (
          <div className="border-2 border-border p-6">
            <pre className="whitespace-pre-wrap text-sm">
              {JSON.stringify(content.content, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b-2 border-border sticky top-0 bg-white z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-black text-lg tracking-tight">CALC AI</span>
          </Link>
          <Link to={`/study/${content.session_id}`}>
            <Button variant="outline" className="h-10 border-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Study
            </Button>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link
            to={`/study/${content.session_id}`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Study Session
          </Link>

          {renderContent()}
        </motion.div>
        {/* Footer */}
        <footer className="border-t-2 border-border py-8 px-6 bg-white">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-sm">CALC AI</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © Powered by FHYAS LLC.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
